import React from "react";
import { useTranslation } from "react-i18next";
import AlertBanner from "../shared/AlertBanner";

interface MultiPersonNameInputProps {
  /** Whether multiple-names mode is enabled */
  multipleNames: boolean;
  /** Whether the name-editing section is visible */
  editNames: boolean;
  /** Current value of the "add name" text input */
  addName: string;
  /** Validation error message for the add-name input */
  addNameError: string;
  /** Whether more names are held than there are guests on the booking */
  namesExceedGuests?: boolean;
  /** Callback when the multiple-names checkbox is toggled */
  onToggle: (checked: boolean) => void;
  /** Callback to submit a new name */
  onAddName: () => void;
  /** Callback when the add-name input value changes */
  onAddNameChange: (value: string) => void;
}

/**
 * Multi-person ordering name input section.
 *
 * Renders a checkbox to enable ordering for multiple people and,
 * when enabled, an input field for adding person names.
 */
const MultiPersonNameInput: React.FC<MultiPersonNameInputProps> = ({
  multipleNames,
  editNames,
  addName,
  addNameError,
  namesExceedGuests = false,
  onToggle,
  onAddName,
  onAddNameChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="gcw:mt-bs-4 gcw:mb-bs-4">
      <div>
        <div className="pre-order-info gcw:mb-2 gcw:w-full">
          <label className="gcw:flex gcw:justify-between gcw:items-center gcw:flex-row-reverse multiple-names-checkbox">
            <input type="checkbox" checked={multipleNames} onChange={(e) => onToggle(e.target.checked)} />
            {t("ordering_for_multiple_people", "Order for multiple people")}
          </label>
          <span className="gcw:text-body gcw:text-text-steps">
            {t("multiple_names_info", "You can add names and order for each person.")}
          </span>
        </div>

        <div className={`add-name-section${multipleNames && editNames ? " show" : ""}`}>
          <div className="add-name-input-wrapper">
            <input
              id="addNameInput"
              type="text"
              value={addName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onAddNameChange(e.target.value)}
              placeholder={t("add_name_placeholder", "Who's ordering?")}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && onAddName()}
            />
            <button type="button" className="btn-add-inside" onClick={onAddName}>
              {t("add_button", "Add")}
            </button>
          </div>
          {addNameError && (
            <div
              className="gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:text-error widget-error-panel gcw:p-2 gcw:text-sm gcw:mt-2"
              role="alert"
            >
              {addNameError}
            </div>
          )}
        </div>

        {namesExceedGuests && (
          <AlertBanner
            variant="warning"
            className="gcw:mt-2 widget-error-panel"
            translatedKey="names_exceed_guests"
            messageDefault="You have more names than guests in your booking. Please remove the names you no longer need."
          />
        )}
      </div>
    </div>
  );
};

export default MultiPersonNameInput;
